/**
 * Offer System Data Models
 * 
 * This file contains the default data structures used throughout
 * the offer system components.
 */

/**
 * Default item/card model
 * Used when creating new cards or resetting forms
 */
export const itemModel = {
  // Basic info
  name: '',
  sentence: '',
  body: '',
  historical: '',
  
  // Categorization
  color: '',
  group: null, // Single group ID
  categories: [],
  assets: [],
  tags: [],
  audiences: [],
  
  // Features & Benefits
  features: [],
  benefits: [],

  stretch: 0,
  reputation: 0,
  contribution: 0,
  
  // Scores (0-5 scale)
  scores: {
    education: 0,
    culture: 0,
    leisure: 0,
    residential: 0,
    commercial: 0,
    hospitality: 0,
    retail: 0,
  },
  
  // Display flags
  spotlight: false,
  allowPrefix: false,
  label: '',
}

/**
 * Available color options for cards
 */
export const colors = [
  { title: 'None', color: '' },
  { title: 'Red', color: '#f44336' },
  { title: 'Pink', color: '#e91e63' },
  { title: 'Purple', color: '#9c27b0' },
  { title: 'Deep Purple', color: '#673ab7' },
  { title: 'Indigo', color: '#3f51b5' },
  { title: 'Blue', color: '#2196f3' },
  { title: 'Light Blue', color: '#03a9f4' },
  { title: 'Cyan', color: '#00bcd4' },
  { title: 'Teal', color: '#009688' },
  { title: 'Green', color: '#4caf50' },
  { title: 'Light Green', color: '#8bc34a' },
  { title: 'Lime', color: '#cddc39' },
  { title: 'Yellow', color: '#ffeb3b' },
  { title: 'Amber', color: '#ffc107' },
  { title: 'Orange', color: '#ff9800' },
  { title: 'Deep Orange', color: '#ff5722' },
  { title: 'Brown', color: '#795548' },
  { title: 'Grey', color: '#9e9e9e' },
  { title: 'Blue Grey', color: '#607d8b' },
]

/**
 * Score field definitions
 */
export const scoreFields = [
  { key: 'education', label: 'Education', color: 'blue' },
  { key: 'culture', label: 'Culture', color: 'purple' },
  { key: 'leisure', label: 'Leisure', color: 'green' },
  { key: 'residential', label: 'Residential', color: 'orange' },
  { key: 'commercial', label: 'Commercial', color: 'red' },
  { key: 'hospitality', label: 'Hospitality', color: 'teal' },
  { key: 'retail', label: 'Retail', color: 'amber' },
]

/**
 * Default group/section model
 */
export const groupModel = {
  id: '',
  title: '',
  name: '',
  children: [],
}

/**
 * Default document model for the exercise
 */
export const docModel = {
  cols: '4',
  list: [],
  deletedGroups: [], // Stores deleted groups with full structure for recovery
  title: '',
  controls: true,
  theme: 'light',
  type: 'large',
  note: false,
  tags: false,
  graph: false,
  contribution: true,
  children: true, // Enable nested children by default
  stats: false,
  edit: true,
  logos: false,
  historic: false,
  local: {
    audiences: [],
    categories: [],
    assets: [],
    tags: [],
  },
}

/**
 * Utility: Deep clone an object
 */
export const deepClone = (obj) => {
  if (obj === null || obj === undefined) return obj
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Utility: Generate a unique ID
 */
export const generateId = (prefix = 'id') => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Utility: Convert string to slug
 */
export const toSlug = (str) => {
  return (str || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')
}